import { defineStore, acceptHMRUpdate } from 'pinia';
import {
    DEFAULT_LEO_PRESENTATION_MODE,
    isAvailableLeoPresentationMode,
    LEGACY_LEO_FULL_PAGE_PARAM,
    LEO_PRESENTATION_MODE_PARAM,
    LEO_PRESENTATION_MODE_STORAGE_KEY,
    LEO_PRESENTATION_MODES,
    TRANSIENT_LEO_PRESENTATION_MODES,
} from '@/ask-pendo/utils/leo-presentation-mode.constants';

export function isLeoPresentationMode (value: unknown): boolean {
    return Object.values(LEO_PRESENTATION_MODES).includes(value as string);
}

/** Whether the mode is one Leo may be reopened in. */
export function isPersistableLeoPresentationMode (value: unknown): boolean {
    return isLeoPresentationMode(value) && !TRANSIENT_LEO_PRESENTATION_MODES.includes(value as string);
}

/** The mode chosen this session, or null before the user has chosen one. */
export function loadLeoPresentationModePref (): string | null {
    try {
        const stored = sessionStorage.getItem(LEO_PRESENTATION_MODE_STORAGE_KEY);

        return isPersistableLeoPresentationMode(stored) ? stored : null;
    } catch {
        return null;
    }
}

function persistLeoPresentationModePref (mode: string): void {
    if (!isPersistableLeoPresentationMode(mode)) {
        return;
    }
    try {
        sessionStorage.setItem(LEO_PRESENTATION_MODE_STORAGE_KEY, mode);
    } catch {
        /* quota / private mode */
    }
}

/**
 * Leo's layout used to live in the URL. Links shared while it did should still open the way they
 * were meant to, as should the older ones that used `expanded` to mean full page.
 */
function readLeoPresentationModeFromLocation (): string | null {
    if (typeof window === 'undefined') {
        return null;
    }

    const params = new URLSearchParams(window.location.search);
    const mode = params.get(LEO_PRESENTATION_MODE_PARAM);
    if (isLeoPresentationMode(mode)) {
        return mode;
    }

    return params.has(LEGACY_LEO_FULL_PAGE_PARAM) ? LEO_PRESENTATION_MODES.FULL_PAGE : null;
}

/**
 * The side drawer standing in for an invalid layout carried over in an old link or stored value.
 */
function availableModeOrDefault (mode: string | null): string {
    return isAvailableLeoPresentationMode(mode) ? (mode as string) : DEFAULT_LEO_PRESENTATION_MODE;
}

/**
 * How Leo is framed on screen: full page, docked in the side drawer, floating in a card, or
 * minimized to a pill.
 *
 * This is a layout, not a location, which is why it is held here rather than in the URL. It has to
 * survive navigation — Leo is meant to be carried around the app — while the page underneath goes
 * on rewriting its own query, and it has to be changed by things that must not touch the URL at
 * all, such as Agent Analytics, whose report re-initialises on navigation.
 *
 * The conversation stays in the URL: which conversation is open is worth a history entry and worth
 * sharing, whereas how big someone likes their drawer is neither.
 */
export const useLeoPresentationModeStore = defineStore('leoPresentationMode', {
    state: () => ({
        mode: availableModeOrDefault(readLeoPresentationModeFromLocation() ?? loadLeoPresentationModePref()),
    }),
    actions: {
        /**
         * @param persist pass false for moves Leo makes on the user's behalf — getting out of a
         * drawer's way, or being parked for a moment — so they do not replace the layout the user
         * chose for themselves.
         */
        setMode (mode: string, { persist = true }: { persist?: boolean } = {}) {
            if (!isAvailableLeoPresentationMode(mode)) {
                return;
            }

            if (persist) {
                persistLeoPresentationModePref(mode);
            }

            this.mode = mode;
        },
        /**
         * Closing Leo ends the layout as well as the conversation, so the next one opens in the
         * layout the user chose rather than wherever the last one was left.
         */
        resetToPreference () {
            this.mode = availableModeOrDefault(loadLeoPresentationModePref());
        },
        /**
         * Steps out of full page, keeping the user's own layout where that is still somewhere Leo
         * can be seen alongside the page. Not persisted: full page is still what they chose, and
         * this is only Leo getting out of the way of a page they have just moved to.
         */
        leaveFullPage () {
            const preferred = loadLeoPresentationModePref();

            this.mode = preferred === LEO_PRESENTATION_MODES.FULL_PAGE
                ? DEFAULT_LEO_PRESENTATION_MODE
                : availableModeOrDefault(preferred);
        },
    },
});

if (module.hot) {
    module.hot.accept(acceptHMRUpdate(useLeoPresentationModeStore, module.hot));
}
